import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components/ui/AnimatedCard';
import { FileText, CreditCard, AlertTriangle, Scale, Mail } from 'lucide-react';

export function TermsOfService() {
  const lastUpdated = 'December 7, 2025';
  const effectiveDate = 'December 7, 2025';

  return (
    <Container size="lg" className="py-6">
      <Stack spacing="lg">
        {/* Header */}
        <FadeIn>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-purple-100 p-3">
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </FadeIn>

        {/* Quick Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              These Terms of Service ("Terms") govern your use of GodMode. By using our service,
              you agree to these terms. Please read them carefully, especially the sections on
              subscriptions, payments, and cancellations.
            </p>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card>
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { id: 'acceptance', label: '1. Acceptance of Terms' },
                { id: 'description', label: '2. Service Description' },
                { id: 'accounts', label: '3. User Accounts' },
                { id: 'subscriptions', label: '4. Subscriptions & Pricing' },
                { id: 'payments', label: '5. Payments & Billing' },
                { id: 'cancellation', label: '6. Cancellation & Refunds' },
                { id: 'free-trial', label: '7. Free Trial' },
                { id: 'acceptable-use', label: '8. Acceptable Use' },
                { id: 'intellectual-property', label: '9. Intellectual Property' },
                { id: 'disclaimers', label: '10. Disclaimers' },
                { id: 'limitation', label: '11. Limitation of Liability' },
                { id: 'termination', label: '12. Termination' },
                { id: 'changes', label: '13. Changes to Terms' },
                { id: 'governing-law', label: '14. Governing Law' },
                { id: 'contact', label: '15. Contact Us' },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Terms Sections */}
        <StaggerContainer>
          <Stack spacing="md">
            {/* 1. Acceptance of Terms */}
            <StaggerItem>
              <Card id="acceptance">
                <CardHeader>
                  <CardTitle>1. Acceptance of Terms</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    By accessing or using GodMode ("Service"), you agree to be bound by these Terms
                    of Service and our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                    If you do not agree to these terms, please do not use the Service.
                  </p>
                  <p>
                    You must be at least 13 years old to use this Service. If you are under 18,
                    you must have parental consent.
                  </p>
                  <p>
                    <strong>Effective Date:</strong> {effectiveDate}
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 2. Service Description */}
            <StaggerItem>
              <Card id="description">
                <CardHeader>
                  <CardTitle>2. Service Description</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    GodMode is a productivity application that helps you manage aspirations, goals,
                    lists, and schedules. The Service includes:
                  </p>
                  <ul>
                    <li>Goal and aspiration tracking</li>
                    <li>List and task management</li>
                    <li>Calendar and scheduling features</li>
                    <li>AI-powered assistance (requires API key or subscription)</li>
                    <li>Cloud backup options</li>
                    <li>Reports and analytics</li>
                  </ul>
                  <p>
                    We reserve the right to modify, suspend, or discontinue any part of the Service
                    at any time with reasonable notice.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 3. User Accounts */}
            <StaggerItem>
              <Card id="accounts">
                <CardHeader>
                  <CardTitle>3. User Accounts</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>To access certain features, you must create an account. You agree to:</p>
                  <ul>
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                  <p>
                    We reserve the right to suspend or terminate accounts that violate these Terms
                    or engage in fraudulent activity.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 4. Subscriptions & Pricing */}
            <StaggerItem>
              <Card id="subscriptions">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    4. Subscriptions & Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">4.1 Subscription Plans</h4>
                  <p>GodMode offers the following subscription options:</p>
                  <ul>
                    <li><strong>Monthly Plan:</strong> £3.99 per month</li>
                    <li><strong>Annual Plan:</strong> £39.99 per year (save £7.89)</li>
                  </ul>

                  <h4 className="text-foreground font-medium">4.2 What's Included</h4>
                  <p>Pro subscribers receive:</p>
                  <ul>
                    <li>Unlimited aspirations, goals, and lists</li>
                    <li>All AI-powered features (form assistance, schedule optimizer, reports)</li>
                    <li>Cloud sync across devices</li>
                    <li>Google Drive backup</li>
                    <li>Priority support</li>
                    <li>Access to all future features during subscription</li>
                  </ul>

                  <h4 className="text-foreground font-medium">4.3 Price Changes</h4>
                  <p>
                    We may change subscription prices with 30 days' notice. Price changes will
                    take effect at the start of your next billing cycle. If you do not agree with
                    a price change, you may cancel your subscription before the new price takes effect.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 5. Payments & Billing */}
            <StaggerItem>
              <Card id="payments">
                <CardHeader>
                  <CardTitle>5. Payments & Billing</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">5.1 Payment Processing</h4>
                  <p>
                    All payments are processed securely through Stripe. We do not store your full
                    credit card information. By subscribing, you authorize us to charge your payment
                    method for the subscription fee.
                  </p>

                  <h4 className="text-foreground font-medium">5.2 Billing Cycle</h4>
                  <ul>
                    <li><strong>Monthly:</strong> Billed on the same date each month</li>
                    <li><strong>Annual:</strong> Billed once per year on your subscription anniversary</li>
                  </ul>

                  <h4 className="text-foreground font-medium">5.3 Failed Payments</h4>
                  <p>
                    If a payment fails, we will attempt to process it again and notify you via email.
                    After multiple failed attempts, your subscription may be suspended until payment
                    is resolved. You will retain access to your data but may lose access to Pro features.
                  </p>

                  <h4 className="text-foreground font-medium">5.4 Taxes</h4>
                  <p>
                    Prices are exclusive of applicable taxes. You are responsible for any taxes
                    associated with your subscription based on your location.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 6. Cancellation & Refunds */}
            <StaggerItem>
              <Card id="cancellation">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    6. Cancellation & Refunds
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">6.1 How to Cancel</h4>
                  <p>
                    You may cancel your subscription at any time through:
                  </p>
                  <ul>
                    <li>The <Link to="/settings" className="text-primary hover:underline">Settings</Link> page in your account</li>
                    <li>The Stripe Customer Portal (accessible from Settings)</li>
                    <li>Contacting our support team</li>
                  </ul>

                  <h4 className="text-foreground font-medium">6.2 Effect of Cancellation</h4>
                  <p>
                    When you cancel:
                  </p>
                  <ul>
                    <li>Your subscription will remain active until the end of your current billing period</li>
                    <li>You will not be charged for the next billing cycle</li>
                    <li>After your subscription ends, you will lose access to Pro features</li>
                    <li>Your data will remain accessible, but some features may be limited</li>
                  </ul>

                  <h4 className="text-foreground font-medium">6.3 Refund Policy</h4>
                  <p>
                    We offer refunds under the following circumstances:
                  </p>
                  <ul>
                    <li><strong>Within 14 days of first payment:</strong> Full refund, no questions asked</li>
                    <li><strong>Technical issues:</strong> If the Service is unavailable for extended periods</li>
                    <li><strong>Duplicate charges:</strong> Accidental double billing will be refunded</li>
                  </ul>
                  <p>
                    Refunds for annual subscriptions after the 14-day period are prorated based on
                    the remaining months. To request a refund, contact our support team.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 7. Free Trial */}
            <StaggerItem>
              <Card id="free-trial">
                <CardHeader>
                  <CardTitle>7. Free Trial</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">7.1 Trial Period</h4>
                  <p>
                    New subscribers receive a 14-day free trial with full access to all Pro features.
                    No charge will be made during the trial period.
                  </p>

                  <h4 className="text-foreground font-medium">7.2 Trial Conversion</h4>
                  <p>
                    At the end of the trial:
                  </p>
                  <ul>
                    <li>Your payment method will be charged automatically</li>
                    <li>Your subscription will begin at the selected billing cycle (monthly or annual)</li>
                    <li>You may cancel before the trial ends to avoid charges</li>
                  </ul>

                  <h4 className="text-foreground font-medium">7.3 Trial Eligibility</h4>
                  <p>
                    Free trials are available once per user. We reserve the right to determine
                    trial eligibility and may limit or modify trial offers at any time.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 8. Acceptable Use */}
            <StaggerItem>
              <Card id="acceptable-use">
                <CardHeader>
                  <CardTitle>8. Acceptable Use</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>You agree not to:</p>
                  <ul>
                    <li>Violate any applicable laws or regulations</li>
                    <li>Infringe on intellectual property rights</li>
                    <li>Transmit malware, viruses, or harmful code</li>
                    <li>Attempt to gain unauthorized access to the Service</li>
                    <li>Interfere with or disrupt the Service</li>
                    <li>Use the Service for illegal or fraudulent purposes</li>
                    <li>Share account credentials with others</li>
                    <li>Resell or redistribute the Service without permission</li>
                    <li>Abuse promotional codes or trial offers</li>
                  </ul>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 9. Intellectual Property */}
            <StaggerItem>
              <Card id="intellectual-property">
                <CardHeader>
                  <CardTitle>9. Intellectual Property</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">9.1 Our Property</h4>
                  <p>
                    The Service, including its design, features, and content, is owned by GodMode
                    and protected by intellectual property laws. You may not copy, modify, or
                    distribute our intellectual property without permission.
                  </p>

                  <h4 className="text-foreground font-medium">9.2 Your Content</h4>
                  <p>
                    You retain ownership of all content you create in the Service (aspirations,
                    goals, lists, etc.). You grant us a limited license to store and display your
                    content as necessary to provide the Service.
                  </p>

                  <h4 className="text-foreground font-medium">9.3 Feedback</h4>
                  <p>
                    Any feedback or suggestions you provide may be used by us to improve the
                    Service without compensation or attribution.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 10. Disclaimers */}
            <StaggerItem>
              <Card id="disclaimers">
                <CardHeader>
                  <CardTitle>10. Disclaimers</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
                    KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                  </p>
                  <ul>
                    <li>Merchantability or fitness for a particular purpose</li>
                    <li>Uninterrupted or error-free operation</li>
                    <li>Accuracy or reliability of any content</li>
                    <li>Results obtained from using the Service</li>
                  </ul>
                  <p>
                    AI-powered features provide suggestions and assistance but should not be relied
                    upon as the sole basis for important decisions.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 11. Limitation of Liability */}
            <StaggerItem>
              <Card id="limitation">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="h-5 w-5" />
                    11. Limitation of Liability
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, GODMODE AND ITS AFFILIATES SHALL NOT
                    BE LIABLE FOR:
                  </p>
                  <ul>
                    <li>Indirect, incidental, special, or consequential damages</li>
                    <li>Loss of data, profits, or business opportunities</li>
                    <li>Service interruptions or downtime</li>
                    <li>Actions of third parties</li>
                  </ul>
                  <p>
                    Our total liability shall not exceed the amount you paid for the Service in
                    the 12 months preceding the claim.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 12. Termination */}
            <StaggerItem>
              <Card id="termination">
                <CardHeader>
                  <CardTitle>12. Termination</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <h4 className="text-foreground font-medium">12.1 By You</h4>
                  <p>
                    You may terminate your account at any time by deleting it from your Settings
                    or contacting support.
                  </p>

                  <h4 className="text-foreground font-medium">12.2 By Us</h4>
                  <p>
                    We may suspend or terminate your account if you:
                  </p>
                  <ul>
                    <li>Violate these Terms</li>
                    <li>Engage in fraudulent activity</li>
                    <li>Fail to pay subscription fees</li>
                    <li>Have been inactive for an extended period</li>
                  </ul>

                  <h4 className="text-foreground font-medium">12.3 Effect of Termination</h4>
                  <p>
                    Upon termination, you will lose access to the Service and your account data.
                    We recommend exporting your data before terminating your account.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 13. Changes to Terms */}
            <StaggerItem>
              <Card id="changes">
                <CardHeader>
                  <CardTitle>13. Changes to Terms</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    We may update these Terms from time to time. Significant changes will be
                    communicated via email or through the Service at least 30 days before they
                    take effect.
                  </p>
                  <p>
                    Your continued use of the Service after changes become effective constitutes
                    acceptance of the updated Terms. If you do not agree with the changes, you
                    may cancel your subscription before they take effect.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 14. Governing Law */}
            <StaggerItem>
              <Card id="governing-law">
                <CardHeader>
                  <CardTitle>14. Governing Law</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    These Terms shall be governed by and construed in accordance with applicable
                    laws. Any disputes arising from these Terms or your use of the Service shall
                    be resolved through binding arbitration or in the courts of competent jurisdiction.
                  </p>
                  <p>
                    You agree to resolve disputes individually and waive any right to participate
                    in class actions or class arbitrations.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            {/* 15. Contact Us */}
            <StaggerItem>
              <Card id="contact">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    15. Contact Us
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose prose-sm max-w-none text-muted-foreground">
                  <p>
                    If you have questions about these Terms, please contact us:
                  </p>
                  <ul>
                    <li><strong>Support:</strong> <Link to="/help" className="text-primary hover:underline">Help Center</Link></li>
                  </ul>
                  <p>
                    For billing inquiries, you can also manage your subscription through the
                    Stripe Customer Portal accessible from your <Link to="/settings" className="text-primary hover:underline">Settings</Link>.
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>
          </Stack>
        </StaggerContainer>

        {/* Related Links */}
        <Card>
          <CardHeader>
            <CardTitle>Related Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/privacy-policy"
                className="text-sm text-primary hover:underline"
              >
                Privacy Policy
              </Link>
              <Link
                to="/help"
                className="text-sm text-primary hover:underline"
              >
                Help Center
              </Link>
              <Link
                to="/settings"
                className="text-sm text-primary hover:underline"
              >
                Manage Subscription
              </Link>
            </div>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
